import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import securityApi from '../api/securityApi';
import { ClipboardCheck, Crosshair, LogOut, Menu, X } from 'lucide-react';
import { isAdminUser } from '../utils/auth';
import ComplianceSidebarNav from './compliance/ComplianceSidebarNav';

const navItems = [
  { name: 'Repositories', path: '/repositories' },
  { name: 'Scans', path: '/scans' },
  { name: 'Applications', path: '/applications', icon: Crosshair },
  { name: 'Compliance', path: '/compliance/projects', icon: ClipboardCheck },
  { name: 'Dashboard', path: '/dashboard' },
  { name: 'Blog', path: '/blog' },
  // { name: 'Reports', path: '/reports' },
  { name: 'Settings', path: '/settings' },
];

const SidebarLayout = ({ children }) => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  // Start closed: assuming admin would flash an admin-only nav item at non-admins.
  const [isAdmin, setIsAdmin] = useState(false);
  // Below `md` the sidebar is an off-canvas drawer; at `md` and up it is always visible.
  const [navOpen, setNavOpen] = useState(false);

  // Extract compliance projectId from path if on a compliance project page
  const complianceProjectMatch = location.pathname.match(/^\/compliance\/projects\/([^/]+)/);
  const complianceProjectId = complianceProjectMatch ? complianceProjectMatch[1] : null;

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await securityApi.getCurrentUser();
        setUser(data);
        setIsAdmin(isAdminUser(data));
      } catch {
        setUser(null);
        setIsAdmin(false);
      }
    };
    fetchUser();
  }, []);

  // Close the drawer on navigation, including programmatic ones.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // While the drawer is open, trap page scroll behind it and close on Escape.
  useEffect(() => {
    if (!navOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [navOpen]);

  // Resizing to desktop while the drawer is open would leave the scroll lock stuck on.
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const handleChange = (e) => {
      if (e.matches) setNavOpen(false);
    };
    // Safari < 14 only has the deprecated addListener. Without the fallback
    // this throws inside an effect and, with no error boundary above it,
    // blanks the whole authenticated shell.
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  // Helper to get initials
  const getInitials = (name) => {
    if (!name) return '';
    return name.split(' ').map(part => part[0]).join('').toUpperCase();
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Mobile top bar: the only way to reach the nav below `md` */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 z-30 bg-white border-b border-gray-200 flex items-center gap-3 px-4">
        <button
          type="button"
          onClick={() => setNavOpen(true)}
          aria-label="Open navigation menu"
          aria-expanded={navOpen}
          aria-controls="app-sidebar"
          className="-ml-2 p-2 rounded-lg text-gray-700 hover:bg-gray-100"
        >
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="bg-teal-600 text-white font-bold rounded-lg w-8 h-8 flex items-center justify-center text-lg">F</div>
          <span className="text-gray-900 text-base font-semibold tracking-wide">Fenny</span>
        </div>
      </header>

      {/* Drawer backdrop */}
      {navOpen && (
        <div
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
          className="md:hidden fixed inset-0 bg-black/40 z-30"
        />
      )}

      {/* Sidebar. `invisible` when closed, not just translated off-screen: a
          translated drawer keeps its links (including Log out) in the tab order
          and the a11y tree, so a keyboard user could tab into and trigger them
          with nothing on screen. Costs the slide-out animation, which snaps
          instead; `inert` would preserve it but needs React 19 and
          Safari 15.5+. */}
      <aside
        id="app-sidebar"
        className={`w-64 bg-white border-r border-gray-200 flex flex-col justify-between py-6 px-4 fixed h-full overflow-y-auto overscroll-contain z-40 transition-transform duration-200 ease-out md:z-20 md:visible md:translate-x-0 md:transition-none ${
          navOpen ? 'translate-x-0' : 'invisible -translate-x-full'
        }`}
      >
        <button
          type="button"
          onClick={() => setNavOpen(false)}
          aria-label="Close navigation menu"
          className="md:hidden absolute top-4 right-3 p-2 rounded-lg text-gray-500 hover:bg-gray-100"
        >
          <X className="h-5 w-5" />
        </button>
        <div>
          {/* Logo */}
          <div className="flex items-center mb-10">
            <div className="bg-teal-600 text-white font-bold rounded-lg w-10 h-10 flex items-center justify-center text-2xl mr-3">F</div>
            <span className="text-gray-900 text-xl font-semibold tracking-wide">Fenny</span>
          </div>
          {/* Navigation. Click is delegated so nested compliance links also close the
              drawer, including taps on the route that is already active. */}
          <nav onClick={() => setNavOpen(false)} className="flex flex-col gap-2 mt-8">
            {navItems.map((item) => {
              if (item.name === 'Dashboard' && !isAdmin) return null;
              const isComplianceItem = item.name === 'Compliance';
              const isActive = location.pathname.startsWith(item.path) ||
                (isComplianceItem && location.pathname.startsWith('/compliance'));
              return (
                <React.Fragment key={item.name}>
                  <Link
                    to={item.path}
                    className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                      isActive
                        ? 'bg-teal-50 text-teal-700 border-l-4 border-teal-600' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                  {isComplianceItem && complianceProjectId && (
                    <ComplianceSidebarNav projectId={complianceProjectId} />
                  )}
                </React.Fragment>
              );
            })}
          </nav>
        </div>
        {/* User Info & Logout */}
        <div className="flex items-center justify-between mt-10">
          <div className="flex items-center gap-2">
            <div className="bg-teal-500 text-white font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm">
              {getInitials(user?.name) || 'U'}
            </div>
            <span className="text-gray-700 text-sm font-medium">{user?.name || 'User'}</span>
          </div>
          <button
            onClick={() => securityApi.logout()}
            className="p-2 -mr-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            aria-label="Log out"
            title="Logout"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </aside>
      {/* Main Content. `pt-14` clears the mobile top bar so pages need no changes. */}
      <main className="flex-1 min-h-screen pt-14 md:pt-0 md:ml-64">
        {children}
      </main>
    </div>
  );
};

export default SidebarLayout;
